// persistentState — minimal URL-hash + namespaced-localStorage sync
// for view state that should survive refresh / be bookmarkable.
//
// CRA, not Next.js — there is no router-bound `useSearchParams`
// primitive available, so we read/write the URL HASH (after the #).
// Hash state survives refresh, supports back/forward, and shows in
// the URL bar so deep-links work — same outcomes the operator asked
// for; just no library.
//
// Restore precedence: URL hash → namespaced localStorage → default.
// Writes go to BOTH simultaneously on change, so the URL bar updates
// AND the next session falls back to localStorage if the URL is
// short (e.g. shared without the hash).
//
// Multi-tenant safety: localStorage is namespaced by user id when
// available; the URL hash is per-tab so cross-user contamination
// only happens if a user shares a deep-link, in which case the
// recipient already has the URL state and we'd WANT that to win.
//
// Hydration safety: the initial state reads `location.hash` and
// `localStorage` synchronously — both are browser-only globals.
// CRA renders in the browser (no SSR), so this is safe; if this
// code is ever lifted into Next.js App Router it needs the
// useEffect-after-mount guard.

import { useEffect, useRef, useState, useCallback } from 'react';

// Read the hash as a Record<string, string>.
//   "#tab=inbox&lead=abc" → { tab: "inbox", lead: "abc" }
export function readHash() {
  if (typeof window === 'undefined' || !window.location.hash) return {};
  const h = window.location.hash.replace(/^#/, '');
  const out = {};
  for (const part of h.split('&')) {
    if (!part) continue;
    const i = part.indexOf('=');
    const k = decodeURIComponent(i === -1 ? part : part.slice(0, i));
    const v = decodeURIComponent(i === -1 ? '' : part.slice(i + 1));
    if (k) out[k] = v;
  }
  return out;
}

// Write the hash, merging with the existing keys so we don't clobber
// hash params other hooks own.
//
// Default mode is 'replace' — high-frequency filter/search updates
// (typing in a search box) should NOT flood the browser back-stack
// with an entry per keystroke.
//
// Mode 'push' is used for LOW-frequency navigation changes (top-level
// tab clicks) so browser Back walks through the tabs the way a user
// expects a real app to behave — and so Backspace/mouse-back doesn't
// exit the site on the first press.
export function writeHash(patch, { historyMode = 'replace' } = {}) {
  if (typeof window === 'undefined') return;
  const cur = readHash();
  const next = { ...cur };
  for (const [k, v] of Object.entries(patch)) {
    if (v == null || v === '') delete next[k];
    else next[k] = v;
  }
  const encoded = Object.entries(next)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const newHash = encoded ? `#${encoded}` : '';
  if (newHash === window.location.hash) return;
  const nextUrl = `${window.location.pathname}${window.location.search}${newHash}`;
  try {
    if (historyMode === 'push') {
      // Capture scroll before push so popstate can restore it.
      const scrollY = typeof window.scrollY === 'number' ? window.scrollY : 0;
      window.history.pushState({ sfaScrollY: scrollY }, '', nextUrl);
    } else {
      window.history.replaceState(null, '', nextUrl);
    }
  } catch { /* fine — sandboxed iframe etc. */ }
}

// Build the namespaced localStorage key for the current user.
// Falls back to a plain key when no user id is available (pre-auth),
// so the same value still survives refresh — it just isn't isolated
// between users on the same browser until auth resolves.
export function namespacedKey(key, userId) {
  if (!userId) return `sfa:${key}`;
  return `sfa:${userId}:${key}`;
}

function safeGetLS(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function safeSetLS(key, value) {
  try {
    if (value == null || value === '') window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* quota / privacy mode — fine */ }
}

// usePersistentState — a useState() that bidirectionally syncs to the
// URL hash AND (optionally) to namespaced localStorage.
//
//   const [tab, setTab] = usePersistentState('tab', 'home', {
//     userId: currentUser?.id,
//     persistToLocalStorage: true,
//     validate: (v) => VALID_TABS.has(v) ? v : null,
//   });
//
// Options:
//   userId               — for localStorage namespacing
//   persistToLocalStorage — true: writes to localStorage too (default true)
//   validate(value)      — return a sanitized value, or null to reject
//   debounceMs           — debounce URL writes (default 0 — most callers
//                          change state on user click; debounce only matters
//                          for high-frequency writes like search text)
export function usePersistentState(hashKey, defaultValue, options = {}) {
  const {
    userId = null,
    persistToLocalStorage = true,
    validate = (v) => v,
    debounceMs = 0,
    // 'replace' (default) — URL updates without adding a back-stack
    // entry. Correct for filter/search state that changes on every
    // keystroke. 'push' — each change adds a real back-stack entry.
    // Use for top-level tab clicks so the browser Back button walks
    // through the tabs a user actually visited.
    historyMode = 'replace',
  } = options;

  const lsKey = namespacedKey(hashKey, userId);

  // Initial value — URL hash wins, then namespaced localStorage,
  // then defaultValue. Validate at every layer so corrupt / stale
  // values fall through.
  const [value, setValueRaw] = useState(() => {
    const hash = readHash();
    if (hashKey in hash) {
      const v = validate(hash[hashKey]);
      if (v != null) return v;
    }
    if (persistToLocalStorage) {
      const stored = safeGetLS(lsKey);
      if (stored != null) {
        const v = validate(stored);
        if (v != null) return v;
      }
    }
    return defaultValue;
  });

  // If userId changes (sign-in resolves after the initial render),
  // re-read namespaced localStorage. This is the auth-resolves-late
  // case the spec called out.
  const lastUserId = useRef(userId);
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    if (!persistToLocalStorage || !userId) return;
    // Don't override the URL hash if the user is mid-session on a
    // specific tab — only restore from LS when the current state is
    // the default (i.e. nothing meaningful was set in the URL).
    const hash = readHash();
    if (hashKey in hash) return;
    const stored = safeGetLS(namespacedKey(hashKey, userId));
    if (stored != null) {
      const v = validate(stored);
      if (v != null) setValueRaw(v);
    }
  }, [userId, hashKey, persistToLocalStorage, validate]);

  const writeTimerRef = useRef(null);
  const setValue = useCallback((next) => {
    // Support the functional updater form like useState.
    setValueRaw(prev => {
      const computed = typeof next === 'function' ? next(prev) : next;
      const validated = validate(computed);
      // Schedule the side-effects (URL + LS) after state commit.
      const toWrite = validated != null ? String(validated) : '';
      const doWrite = () => {
        writeHash({ [hashKey]: toWrite || null }, { historyMode });
        if (persistToLocalStorage) safeSetLS(lsKey, toWrite);
      };
      if (debounceMs > 0) {
        if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
        writeTimerRef.current = setTimeout(doWrite, debounceMs);
      } else {
        doWrite();
      }
      return validated != null ? validated : prev;
    });
  }, [hashKey, lsKey, persistToLocalStorage, validate, debounceMs, historyMode]);

  // Listen for hashchange (back/forward, manual edits) — pick up
  // external mutations so the component stays in sync.
  useEffect(() => {
    const onHash = () => {
      const hash = readHash();
      if (!(hashKey in hash)) return;
      const v = validate(hash[hashKey]);
      if (v != null) setValueRaw(v);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [hashKey, validate]);

  // For historyMode='push' consumers (top-level tab): if the initial
  // URL doesn't carry our key, seed it via replaceState on mount. This
  // ensures the FIRST back navigation lands on a URL that still has
  // the tab key — otherwise "click tab → back" would return to a
  // hash-less URL and the hook wouldn't know how to restore. Fires
  // exactly once; harmless if the key is already present.
  const seededRef = useRef(false);
  useEffect(() => {
    if (historyMode !== 'push' || seededRef.current) return;
    seededRef.current = true;
    if (typeof window === 'undefined') return;
    const hash = readHash();
    if (hashKey in hash) return;
    const seed = value == null ? '' : String(value);
    if (!seed) return;
    writeHash({ [hashKey]: seed }, { historyMode: 'replace' });
  }, [hashKey, historyMode, value]);

  // Clean up any pending debounced write on unmount.
  useEffect(() => {
    return () => { if (writeTimerRef.current) clearTimeout(writeTimerRef.current); };
  }, []);

  return [value, setValue];
}
